import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bookmark,
  RotateCw,
  Check,
  ShoppingBag,
  Shirt,
  Footprints,
  Layers,
  Watch,
  Sparkles,
  ThumbsUp,
  AlertTriangle,
  Share2,
  ChevronLeft,
  ChevronRight,
  Shield,
  Flame,
  Zap,
  PiggyBank,
} from "lucide-react";
import TopBar from "@/components/TopBar";
import RatingBar from "@/components/RatingBar";
import ScoreDonut from "@/components/ScoreDonut";
import Skeleton from "@/components/Skeleton";
import { useOutfitDraft } from "@/context/OutfitDraftContext";
import { useToast } from "@/components/Toast";
import api, { formatApiError } from "@/lib/api";
import RealStoreFinds from "@/components/RealStoreFinds";

function priceFmt(n) {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toFixed(0)}`;
}

function colorToCss(name) {
  if (!name) return "#888";
  const n = name.toLowerCase().trim();
  const map = {
    "black":"#0a0a0a","white":"#f5f5f5","ivory":"#f1ece1","cream":"#efe7d3",
    "beige":"#d6c3a5","tan":"#c8a778","camel":"#b58a5a","khaki":"#a08a5b",
    "olive":"#5b6034","navy":"#1c2540","blue":"#3a5aa8","denim":"#3e5b80",
    "gray":"#7a7a7a","grey":"#7a7a7a","charcoal":"#2c2c2c","stone":"#9b9485",
    "brown":"#5a3b22","chocolate":"#3b2418","burgundy":"#5a1a23","wine":"#5a1a23",
    "red":"#a83232","rust":"#a85533","orange":"#c66a2c","mustard":"#c69a2c",
    "yellow":"#d8c14b","green":"#365e3a","forest":"#1f3a26","sage":"#9bae8b",
    "teal":"#1e5d63","purple":"#52366b","pink":"#d59bb0",
  };
  for (const k of Object.keys(map)) if (n.includes(k)) return map[k];
  return "#888";
}

const ITEM_ICON = { top: Shirt, bottom: Layers, outerwear: Layers, shoes: Footprints };

// Visual metadata per variant LABEL — icon, color accent, short subtitle.
const LABEL_META = {
  "Safe Pick":        { icon: Shield,    accent: "#9c9c9c", sub: "Timeless · Low risk" },
  "Trending":         { icon: Flame,     accent: "#ff5a3c", sub: "What's hot now" },
  "Bold":             { icon: Zap,       accent: "#9b6bff", sub: "Make a statement" },
  "Budget Maximizer": { icon: PiggyBank, accent: "#3cc97a", sub: "Best value" },
};

function VariantTabBar({ variants, activeIdx, onPick }) {
  return (
    <div className="variant-tabs" data-testid="results-variant-tabs">
      {variants.map((v, i) => {
        const meta = LABEL_META[v.label] || { icon: Sparkles, accent: "#fff", sub: "" };
        const Icon = meta.icon;
        return (
          <button
            key={v.label + i}
            type="button"
            className={`variant-tab ${i === activeIdx ? "active" : ""}`}
            onClick={() => onPick(i)}
            data-testid={`variant-tab-${i}`}
            style={{ "--accent": meta.accent }}
          >
            <Icon size={13} strokeWidth={2}/>
            <span>{v.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ResultsSkeleton() {
  return (
    <div data-testid="results-loading" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 18, padding: 24, border: "1px solid var(--line-strong)", borderRadius: 24, background: "linear-gradient(180deg, #181818 0%, #0b0b0b 100%)" }}>
        <Skeleton w={84} h={84} r={999} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
          <Skeleton w="50%" h={14} />
          <Skeleton w="80%" h={22} />
          <Skeleton w="40%" h={12} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "20px 0" }}>
        <div className="spinner" />
        <div className="tiny" style={{ letterSpacing: "0.22em", textTransform: "uppercase" }}>
          Curating 4 distinct looks…
        </div>
      </div>
      {[0,1,2,3,4].map((i) => (
        <div key={i} className="rating-row">
          <Skeleton w={110} h={12} />
          <Skeleton w="100%" h={6} r={999} />
          <Skeleton w={36} h={12} />
        </div>
      ))}
      <Skeleton w="100%" h={120} r={18} />
      <Skeleton w="100%" h={80} r={16} />
      <Skeleton w="100%" h={80} r={16} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// One variant — full breakdown card. Receives its own mannequin state and
// per-variant save/share callbacks from the parent.
// -----------------------------------------------------------------------------
function VariantPanel({
  variantIdx,
  variant,
  style,
  budget,
  previewState,
  isSaved,
  saving,
  onSave,
  onShare,
  testIdPrefix,
}) {
  const outfit = variant.outfit || {};
  const items = outfit.items || {};
  const accessories = items.accessories || [];
  const scores = outfit.scores || {};
  const confidence = outfit.confidence_percent;
  const whyWorks = outfit.why_it_works || [];
  const improve = outfit.things_to_improve || [];
  const meta = LABEL_META[variant.label] || { icon: Sparkles, accent: "#fff", sub: "" };
  const Icon = meta.icon;

  const overBudget = budget != null && outfit.total_estimated_price > budget;

  const shopFirst = () => {
    const sl = variant.store_links?.[0];
    const first = sl?.retailers?.[0]?.url || sl?.google;
    if (first) window.open(first, "_blank", "noreferrer");
  };

  return (
    <div className="variant-panel" data-testid={`${testIdPrefix}-${variantIdx}`}>
      {/* Variant badge header */}
      <div className="variant-badge" style={{ "--accent": meta.accent }} data-testid={`${testIdPrefix}-${variantIdx}-badge`}>
        <Icon size={14} strokeWidth={2.2}/>
        <span className="variant-badge-label">{variant.label}</span>
        <span className="variant-badge-sub">{meta.sub}</span>
      </div>

      {variant.tagline && (
        <div className="variant-tagline" data-testid={`${testIdPrefix}-${variantIdx}-tagline`}>
          {variant.tagline}
        </div>
      )}

      {/* Score hero */}
      <div className="score-hero" data-testid={`${testIdPrefix}-${variantIdx}-score-hero`}>
        <ScoreDonut score={scores.overall ?? 0} testId={`${testIdPrefix}-${variantIdx}-score-donut`} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>{(style || "outfit").toUpperCase()}</div>
          <div className="h2" style={{ fontSize: 22, lineHeight: 1.15 }} data-testid={`${testIdPrefix}-${variantIdx}-title`}>
            {outfit.title || variant.label}
          </div>
          {confidence != null && (
            <div style={{ marginTop: 10 }}>
              <span className="confidence-pill" data-testid={`${testIdPrefix}-${variantIdx}-confidence`}>
                <span className="dot" />
                <span className="numeric">{Math.round(confidence)}% Match</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* AI Summary */}
      {outfit.summary && (
        <div className="outfit-section">
          <div className="eyebrow" style={{ marginBottom: 10 }}>AI SUMMARY</div>
          <p className="body" style={{ color: "#fff" }} data-testid={`${testIdPrefix}-${variantIdx}-summary`}>
            {outfit.summary}
          </p>
        </div>
      )}

      {/* Mannequin preview */}
      <div className="outfit-section">
        <div className="eyebrow" style={{ marginBottom: 12 }}>OUTFIT PREVIEW</div>
        <div className="outfit-preview-card" data-testid={`${testIdPrefix}-${variantIdx}-preview`}>
          {previewState?.loading && (
            <div className="outfit-preview-loading">
              <div className="spinner" style={{ marginBottom: 14 }} />
              <div className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
                Rendering this look…
              </div>
              <div className="body-sm" style={{ marginTop: 6, color: "rgba(255,255,255,0.4)" }}>
                Building a personalised model in your proportions
              </div>
            </div>
          )}
          {!previewState?.loading && previewState?.image && (
            <img
              src={previewState.image}
              alt={`${variant.label} preview`}
              className="outfit-preview-img"
              data-testid={`${testIdPrefix}-${variantIdx}-preview-img`}
            />
          )}
          {!previewState?.loading && !previewState?.image && previewState?.error && (
            <div className="outfit-preview-loading" data-testid={`${testIdPrefix}-${variantIdx}-preview-error`}>
              <div className="tiny" style={{ letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
                Preview unavailable
              </div>
              <div className="body-sm" style={{ marginTop: 6, color: "rgba(255,255,255,0.45)" }}>
                The image service didn&apos;t respond. The text details below are still spot-on.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stylist note */}
      {outfit.personalized_reasoning && (
        <div className="outfit-section stylist-note">
          <div className="eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={12} strokeWidth={1.8} /> WHY THIS LOOKS GOOD ON YOU
          </div>
          <p className="body" style={{ color: "#fff" }} data-testid={`${testIdPrefix}-${variantIdx}-reasoning`}>
            {outfit.personalized_reasoning}
          </p>
        </div>
      )}

      {/* Styling tips */}
      {outfit.styling_tips?.length > 0 && (
        <div className="outfit-section">
          <div className="eyebrow" style={{ marginBottom: 12 }}>STYLING TIPS</div>
          <ul className="bullet-list">
            {outfit.styling_tips.map((t, i) => (
              <li key={i} data-testid={`${testIdPrefix}-${variantIdx}-tip-${i}`}>{t}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Ratings */}
      {Object.keys(scores).length > 0 && (
        <div className="outfit-section">
          <div className="eyebrow" style={{ marginBottom: 14 }}>OUTFIT RATING</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {scores.style != null && <RatingBar label="Style" score={scores.style} testId={`${testIdPrefix}-${variantIdx}-rating-style`} />}
            {scores.fit != null && <RatingBar label="Fit" score={scores.fit} testId={`${testIdPrefix}-${variantIdx}-rating-fit`} />}
            {scores.color_match != null && <RatingBar label="Color Match" score={scores.color_match} testId={`${testIdPrefix}-${variantIdx}-rating-color`} />}
            {scores.trendiness != null && <RatingBar label="Trendiness" score={scores.trendiness} testId={`${testIdPrefix}-${variantIdx}-rating-trend`} />}
            {scores.versatility != null && <RatingBar label="Versatility" score={scores.versatility} testId={`${testIdPrefix}-${variantIdx}-rating-versatility`} />}
          </div>
        </div>
      )}

      {/* Palette */}
      {outfit.color_palette?.length > 0 && (
        <div className="outfit-section">
          <div className="eyebrow" style={{ marginBottom: 14 }}>COLOR PALETTE</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, rowGap: 10 }}>
            {outfit.color_palette.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="color-dot" style={{ background: colorToCss(c) }} />
                <span className="body-sm" style={{ color: "#fff" }}>{c}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Items */}
      {(items.top || items.bottom || items.outerwear || items.shoes || accessories.length > 0) && (
        <div className="outfit-section">
          <div className="eyebrow" style={{ marginBottom: 14 }}>RECOMMENDED ITEMS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {["top", "bottom", "outerwear", "shoes"].map((k) => {
              const it = items[k];
              if (!it || !it.name) return null;
              const ItemIcon = ITEM_ICON[k] || Shirt;
              const itemFallbacks = variant.item_retailers?.[it.name]?.retailers;
              return (
                <div key={k} className="item-card-wrap">
                  <div className="item-card" data-testid={`${testIdPrefix}-${variantIdx}-item-${k}`}>
                    <div className="item-thumb"><ItemIcon size={18} strokeWidth={1.6} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                        <div className="h3" style={{ color: "#fff" }}>{it.name}</div>
                        {it.estimated_price != null && (
                          <span className="price-pill numeric">{priceFmt(it.estimated_price)}</span>
                        )}
                      </div>
                      <div className="tiny" style={{ letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 4, color: "rgba(255,255,255,0.5)" }}>
                        {k}
                      </div>
                      <div className="body-sm" style={{ marginTop: 8 }}>{it.description}</div>
                    </div>
                  </div>
                  <RealStoreFinds
                    query={it.name}
                    limit={6}
                    fallbackRetailers={itemFallbacks}
                    testId={`${testIdPrefix}-${variantIdx}-finds-${k}`}
                  />
                </div>
              );
            })}
            {accessories.map((a, i) => {
              const accFallbacks = variant.item_retailers?.[a.name]?.retailers;
              return (
                <div key={`acc-${i}`} className="item-card-wrap">
                  <div className="item-card" data-testid={`${testIdPrefix}-${variantIdx}-accessory-${i}`}>
                    <div className="item-thumb"><Watch size={18} strokeWidth={1.6} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                        <div className="h3" style={{ color: "#fff" }}>{a.name}</div>
                        {a.estimated_price != null && (
                          <span className="price-pill numeric">{priceFmt(a.estimated_price)}</span>
                        )}
                      </div>
                      <div className="tiny" style={{ letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 4, color: "rgba(255,255,255,0.5)" }}>
                        Accessory
                      </div>
                      <div className="body-sm" style={{ marginTop: 8 }}>{a.description}</div>
                    </div>
                  </div>
                  <RealStoreFinds
                    query={a.name}
                    limit={6}
                    fallbackRetailers={accFallbacks}
                    testId={`${testIdPrefix}-${variantIdx}-finds-accessory-${i}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {whyWorks.length > 0 && (
        <div className="outfit-section">
          <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <ThumbsUp size={12} strokeWidth={1.8} /> WHY THIS WORKS
          </div>
          <ul className="bullet-list">
            {whyWorks.map((line, i) => <li key={i} data-testid={`${testIdPrefix}-${variantIdx}-why-${i}`}>{line}</li>)}
          </ul>
        </div>
      )}

      {improve.length > 0 && (
        <div className="outfit-section">
          <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <AlertTriangle size={12} strokeWidth={1.8} /> THINGS TO IMPROVE
          </div>
          <ul className="bullet-list warn">
            {improve.map((line, i) => <li key={i} data-testid={`${testIdPrefix}-${variantIdx}-improve-${i}`}>{line}</li>)}
          </ul>
        </div>
      )}

      {/* Total */}
      {outfit.total_estimated_price != null && (
        <div className="outfit-section" data-testid={`${testIdPrefix}-${variantIdx}-total`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div className="eyebrow">ESTIMATED TOTAL</div>
            <div className="h2 numeric" style={{ fontSize: 32, color: overBudget ? "var(--danger)" : "#fff" }}>
              {priceFmt(outfit.total_estimated_price)}
            </div>
          </div>
          {overBudget && (
            <div className="tiny" style={{ color: "var(--danger)", marginTop: 6, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              ${(outfit.total_estimated_price - budget).toFixed(0)} over your ${budget} budget
            </div>
          )}
        </div>
      )}

      {/* Per-variant actions */}
      <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
        <button
          className="btn btn-primary"
          onClick={() => onSave(variantIdx)}
          disabled={saving || isSaved}
          data-testid={`${testIdPrefix}-${variantIdx}-save-btn`}
        >
          {isSaved
            ? (<><Check size={16}/> Saved to wardrobe</>)
            : (<><Bookmark size={16}/> {saving ? "Saving…" : `Save ${variant.label}`}</>)}
        </button>
        <button
          className="btn btn-outline"
          onClick={() => onShare(variantIdx)}
          disabled={saving || !previewState?.image}
          data-testid={`${testIdPrefix}-${variantIdx}-share-btn`}
        >
          <Share2 size={16}/> {previewState?.image ? "Share to Fashion Feed" : "Preparing share…"}
        </button>
        <button
          className="btn btn-outline"
          onClick={shopFirst}
          disabled={!variant.store_links?.length}
          data-testid={`${testIdPrefix}-${variantIdx}-shop-btn`}
        >
          <ShoppingBag size={16}/> Shop similar items
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main screen: header + tab bar + horizontal swipe carousel + bottom remix CTA.
// -----------------------------------------------------------------------------
export default function ResultsScreen() {
  const navigate = useNavigate();
  const { draft } = useOutfitDraft();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Per-variant mannequin state — { 0: {loading, image, error}, 1: {...}, ... }
  const [previews, setPreviews] = useState({});
  // Per-variant saved-doc state — { idx: savedDoc }
  const [savedByIdx, setSavedByIdx] = useState({});
  const [savingIdx, setSavingIdx] = useState(null);

  const ranRef = useRef(false);
  const previewRunRef = useRef(new Set());
  const carouselRef = useRef(null);
  const slotRefs = useRef([]);

  const variants = data?.variants || [];

  const generate = useCallback(async () => {
    setLoading(true);
    setError("");
    setData(null);
    setPreviews({});
    setSavedByIdx({});
    setActiveIdx(0);
    previewRunRef.current = new Set();
    try {
      const body = {
        image_base64: draft.imageBase64 || null,
        image_mime: draft.imageMime || "image/jpeg",
        style: draft.style || "casual",
        budget: draft.budget ? parseFloat(draft.budget) : null,
        height: draft.height || null,
        weight: draft.weight || null,
        skin_tone: draft.skinTone || null,
        gender: draft.gender || null,
        celebrity: draft.celebrity || null,
        mode: draft.mode || "upload",
        appearance: draft.appearance || null,
      };
      const { data: res } = await api.post("/outfit/generate", body);
      setData(res);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [draft]);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!draft.style) {
      navigate("/style-pick", { replace: true });
      return;
    }
    generate();
  }, [draft.style, generate, navigate]);

  // Lazy-load the mannequin preview for the active variant (and its neighbors).
  const ensurePreview = useCallback(
    async (idx) => {
      if (idx < 0 || idx >= variants.length) return;
      if (previewRunRef.current.has(idx)) return;
      const variant = variants[idx];
      if (!variant?.outfit) return;
      previewRunRef.current.add(idx);
      setPreviews((p) => ({ ...p, [idx]: { ...(p[idx] || {}), loading: true, error: false } }));
      try {
        const { data: img } = await api.post("/outfit/preview-image", {
          outfit: variant.outfit,
          appearance: draft.appearance || null,
          gender: draft.gender || null,
          height: draft.height || null,
          weight: draft.weight || null,
          skin_tone: draft.skinTone || null,
          style: data?.style,
        });
        if (img?.image_base64) {
          const mime = img.mime_type || "image/png";
          setPreviews((p) => ({
            ...p,
            [idx]: { loading: false, image: `data:${mime};base64,${img.image_base64}` },
          }));
        } else {
          setPreviews((p) => ({ ...p, [idx]: { loading: false, error: true } }));
        }
      } catch {
        setPreviews((p) => ({ ...p, [idx]: { loading: false, error: true } }));
      }
    },
    [variants, draft, data]
  );

  // Trigger preview for the active variant whenever it changes.
  useEffect(() => {
    if (!variants.length) return;
    ensurePreview(activeIdx);
  }, [variants.length, ensurePreview, activeIdx]);

  // Detect which slot is most-visible inside the horizontal scroll container.
  useEffect(() => {
    if (!variants.length) return;
    const root = carouselRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        let bestIdx = activeIdx;
        let bestRatio = 0;
        for (const e of entries) {
          const idx = Number(e.target.dataset.idx);
          if (e.intersectionRatio > bestRatio) { bestRatio = e.intersectionRatio; bestIdx = idx; }
        }
        if (bestRatio >= 0.55) setActiveIdx(bestIdx);
      },
      { root, threshold: [0.4, 0.6, 0.8, 1] }
    );
    slotRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [variants.length, activeIdx]);

  const scrollToVariant = (idx) => {
    const root = carouselRef.current;
    const el = slotRefs.current[idx];
    if (!root || !el) return;
    root.scrollTo({ left: el.offsetLeft, behavior: "smooth" });
    setActiveIdx(idx);
  };

  // -----------------------------------------------------------------
  // Save / Share per variant
  // -----------------------------------------------------------------
  const saveVariant = async (idx) => {
    const variant = variants[idx];
    if (!variant?.outfit) return;
    if (savedByIdx[idx]) return;
    setSavingIdx(idx);
    try {
      const preview = previews[idx]?.image;
      const previewB64 = preview ? preview.split(",", 2)[1] : null;
      const previewMime = preview ? (preview.match(/^data:([^;]+);/)?.[1] || "image/png") : "image/png";
      const { data: saved } = await api.post("/outfits/save", {
        outfit: variant.outfit,
        style: data.style,
        thumbnail_base64: draft.imageBase64 || null,
        preview_image_base64: previewB64,
        preview_image_mime: previewMime,
      });
      setSavedByIdx((p) => ({ ...p, [idx]: saved }));
      toast.show(`Saved · ${variant.label}`, "success");
    } catch (err) {
      toast.show(formatApiError(err) || "Couldn't save. Try again.", "error");
    } finally {
      setSavingIdx(null);
    }
  };

  const shareVariant = async (idx) => {
    const variant = variants[idx];
    if (!variant?.outfit) return;
    let outfitId = savedByIdx[idx]?.id;
    if (!outfitId) {
      const preview = previews[idx]?.image;
      if (!preview) {
        toast.show("AI preview is still rendering — try again in a moment.", "error");
        return;
      }
      setSavingIdx(idx);
      try {
        const previewB64 = preview.split(",", 2)[1];
        const { data: saved } = await api.post("/outfits/save", {
          outfit: variant.outfit,
          style: data.style,
          thumbnail_base64: draft.imageBase64 || null,
          preview_image_base64: previewB64,
          preview_image_mime: "image/png",
        });
        setSavedByIdx((p) => ({ ...p, [idx]: saved }));
        outfitId = saved.id;
      } catch (err) {
        setSavingIdx(null);
        toast.show(formatApiError(err) || "Couldn't save outfit.", "error");
        return;
      } finally { setSavingIdx(null); }
    }
    try {
      await api.post(`/feed/share-outfit/${outfitId}`, {
        caption: `${variant.label} · ${data.style || "trending"}`,
        style_tags: [data.style, variant.label.toLowerCase().replace(/\s+/g, "_")].filter(Boolean),
        gender_tag: draft.gender || null,
      });
      toast.show(`Shared · ${variant.label}`, "success");
      setTimeout(() => navigate("/feed"), 500);
    } catch (err) {
      toast.show(formatApiError(err) || "Couldn't share to feed.", "error");
    }
  };

  const budgetNum = draft.budget ? parseFloat(draft.budget) : null;
  const activeVariantLabel = variants[activeIdx]?.label || "";

  // Memoize meta lookups
  const activeMeta = useMemo(
    () => LABEL_META[activeVariantLabel] || { accent: "#fff" },
    [activeVariantLabel]
  );

  return (
    <div className="screen fade-in" data-testid="results-screen" style={{ "--active-accent": activeMeta.accent }}>
      <TopBar
        title="YOUR OUTFITS"
        right={
          <button
            type="button"
            className="icon-btn"
            onClick={generate}
            aria-label="Remix all 4"
            disabled={loading}
            data-testid="results-regenerate-btn"
          >
            <RotateCw size={16} strokeWidth={1.7} />
          </button>
        }
      />

      {loading && <ResultsSkeleton />}

      {error && !loading && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 18 }} data-testid="results-error">
          <div className="eyebrow" style={{ color: "var(--danger)" }}>SOMETHING WENT WRONG</div>
          <p className="body" style={{ color: "#fff" }}>{error}</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-outline" onClick={generate} data-testid="results-retry-btn">Try again</button>
            <button className="btn btn-ghost" onClick={() => navigate("/home")} data-testid="results-home-btn">Back home</button>
          </div>
        </div>
      )}

      {!loading && !error && variants.length > 0 && (
        <>
          {/* Eyebrow + count */}
          <div className="variant-header" data-testid="results-variant-header">
            <div className="eyebrow">{variants.length} LOOKS FOR YOU</div>
            <div className="tiny" style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
              Swipe to compare
            </div>
          </div>

          {/* Tab bar */}
          <VariantTabBar variants={variants} activeIdx={activeIdx} onPick={scrollToVariant} />

          {/* Swipe carousel */}
          <div className="variant-carousel" ref={carouselRef} data-testid="results-carousel">
            {variants.map((v, i) => (
              <div
                key={v.label + i}
                className="variant-slot"
                data-idx={i}
                ref={(el) => (slotRefs.current[i] = el)}
              >
                <VariantPanel
                  variantIdx={i}
                  variant={v}
                  style={data.style}
                  budget={budgetNum}
                  previewState={previews[i]}
                  isSaved={!!savedByIdx[i]}
                  saving={savingIdx === i}
                  onSave={saveVariant}
                  onShare={shareVariant}
                  testIdPrefix="results-variant"
                />
              </div>
            ))}
          </div>

          {/* Carousel pagination + nav arrows */}
          <div className="variant-pagination" data-testid="results-pagination">
            <button
              type="button"
              className="icon-btn"
              onClick={() => scrollToVariant(Math.max(0, activeIdx - 1))}
              disabled={activeIdx === 0}
              aria-label="Previous look"
              data-testid="results-prev-btn"
            >
              <ChevronLeft size={18}/>
            </button>
            <div className="variant-dots">
              {variants.map((v, i) => (
                <button
                  key={i}
                  type="button"
                  className={`variant-dot ${i === activeIdx ? "active" : ""}`}
                  onClick={() => scrollToVariant(i)}
                  aria-label={`Go to ${v.label}`}
                  data-testid={`results-dot-${i}`}
                />
              ))}
            </div>
            <button
              type="button"
              className="icon-btn"
              onClick={() => scrollToVariant(Math.min(variants.length - 1, activeIdx + 1))}
              disabled={activeIdx === variants.length - 1}
              aria-label="Next look"
              data-testid="results-next-btn"
            >
              <ChevronRight size={18}/>
            </button>
          </div>

          {/* Bottom global action: remix ALL four */}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={generate}
            data-testid="results-remix-all-btn"
            style={{ marginTop: 18 }}
          >
            <Sparkles size={16}/> Remix all 4 looks
          </button>

          {data.remaining != null && (
            <div className="tiny" style={{ marginTop: 14, textAlign: "center", letterSpacing: "0.20em", textTransform: "uppercase" }}>
              {data.remaining} free outfit{data.remaining === 1 ? "" : "s"} left today
            </div>
          )}
        </>
      )}
    </div>
  );
}
